import { useEffect, useState } from "react";
import { useMatchFilterOptions } from "../hooks/useMatchFilterOptions";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { getWithRetry } from "../lib/fetchWithRetry";
import LoadFailureNote from "../components/LoadFailureNote";
import { UpcomingAddMenu } from "../components/UpcomingAddMenu";
import { AI_COACH_ENABLED, newUi } from "../lib/featureFlags";
import { coachPlayersName } from "../lib/coachPlayersName";
import { computeRecordGate, computeRecordFailedGate, computeSeasonRecordGate } from "../lib/recordGate";
import { useAuth } from "../context/AuthContext";
import { OnboardingResumePrompt } from "../components/OnboardingResumePrompt";
import FreePeriodEndedNotice from "../components/FreePeriodEndedNotice";
// Issue #760 — the empty record. `RecordEmptyScreen` owns the whole page
// (its own navy TopBar/destination row, no shipped chrome) while the
// player's account has zero matches and no filter is active, behind
// `newUi("record")`. See that file's own header for why this is an early
// return, mirroring `LogGroundFlow.jsx`'s "the rendered screen replaces the
// page" pattern (#803's decision on #757) one route over.
import RecordEmptyScreen from "../components/RecordEmptyScreen";
// Issue #761 — the record after one match. Owns its own hero/avg/bowling
// content while `showRecordOne` is true; see this file's own comment on
// that gate for the three-way split (recent matches pending/failed/ready)
// this screen alone needs, on top of #760's `recordLoading`/`showEmptyRecord`.
import RecordOneMatchScreen from "../components/RecordOneMatchScreen";
// Issue #762 — the season record. Owns the whole page (its own filter bar,
// hero/ratio tiles, fielding/keeping split, five deep-analytics folds and
// match list) once the account holds two or more matches, behind
// `newUi("season-record")` — a screen id of its own, separate from #760/
// #761's `newUi("record")`. D149 (PR #880 review) — `newUi("season-record")`
// itself now resolves true only when `newUi("record")` is ALSO on
// (`lib/featureFlags.js`), because the season record depends on #760/#761
// having already established the redrawn record identity at 0/1 matches;
// see `lib/recordGate.js`'s own header on `computeSeasonRecordGate`, which
// also takes an explicit `recordScreenOn` param and ANDs it in
// independently (that function is a plain, non-React unit and cannot call
// `newUi` itself).
import SeasonRecordScreen from "../components/SeasonRecordScreen";
import CareerDashboard from "./CareerDashboard";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Target, TrendingUp, Activity, Trophy, ArrowRight, Calendar,
  BarChart3, MapPinned, Bot, User as UserIcon, MapPin, Clock,
  Sparkles, ChevronRight, AlertCircle, Users, NotebookPen, ClipboardList,
} from "lucide-react";
import { LineChart, Line, ResponsiveContainer, Tooltip } from "recharts";
import { dateTimeLabel } from "../lib/clockTime";
import { counterText } from "../lib/counters";
import { clientDateHeaders } from "../lib/trainingStatus";
import { avgText } from "../lib/recordFigures";

const NAV_CARDS = [
  { to: "/statistics", label: "Statistics", icon: BarChart3, color: "#0B2E59" },
  { to: "/development", label: "Development", icon: Target, color: "#D4A017" },
  { to: "/grounds", label: "Grounds", icon: MapPinned, color: "#84C5FF" },
  // Coach AI is flagged off by default (spec §7/D6, §8.1; issue #56) — no nav
  // entry to a page that would otherwise redirect straight back to
  // "/dashboard".
  ...(AI_COACH_ENABLED ? [{ to: "/coaching-insights", label: "Coach AI", icon: Bot, color: "#0B2E59" }] : []),
];

// Query-string builder shared by the summary fetch — omits a param
// entirely for "all" so untouched behaviour (today's exact totals) is
// unchanged, per issue #55 (season/competition) and #78 (team).
function toQuery(season, competition, team) {
  const params = new URLSearchParams();
  if (season !== "all") params.set("season", season);
  if (competition !== "all") params.set("competition", competition);
  if (team !== "all") params.set("team", team);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * #1674: the free-period notice rides above whichever screen the page picks (RecordEmpty,
 * RecordOneMatch, SeasonRecord, or the populated dashboard), so all of them get it. It draws nothing
 * for an admin, a coach, or anyone who is not past_due with no card.
 */
export default function Dashboard() {
  return (
    <>
      <FreePeriodEndedNotice />
      <DashboardScreen />
    </>
  );
}

function DashboardScreen() {
  const { user } = useAuth();
  // Issue #74: the player summary/log-match dashboard below is role-blind —
  // it was being shown to coaches too, offering "Log a match" / "Log
  // training" affordances the API already 403s for any non-player
  // (api/src/routes/matches.ts:268,298,334). Scoped to "coach" specifically
  // (not `role !== "player"`) so admin's existing dashboard is untouched —
  // admin isn't part of this bug report and isn't covered by the acceptance
  // criteria, so its behavior is left exactly as-is pending a separate call.
  const isCoach = user?.role === "coach";
  // Issue #226 slice 1: admin's first real landing surface. Scoped to
  // "admin" specifically, same reasoning as isCoach above — the
  // player-scoped effects below assume a caller-owned player profile
  // (`/players/me`, `/goals` etc. with no player_id, which resolveScope
  // 400s for a non-player caller with no id supplied) and would error for
  // admin exactly as they do for coach.
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState(null);
  const [goals, setGoals] = useState([]);
  const [matches, setMatches] = useState([]);
  const [player, setPlayer] = useState(null);
  // Coach Feedback tile (issue #184 / SPEC-pilot-polish.md §3) — `notes`
  // reuses the same `/notes` read DevelopmentPlan.jsx's feedback tab already
  // calls (no role gate on reads, scoped to the caller's own profile via
  // resolveScope). Paired with `player.coach_feedback_seen_at` (added to
  // GET /players/me in this same slice) to compute "unread" entirely
  // client-side — no new round trip, per Martin's PR #192 answer 4.
  const [notes, setNotes] = useState([]);
  // #83. Prefer the count the API computed; fall back to the list's own
  // length so an older cached response still renders the prompt.
  const [upcoming, setUpcoming] = useState({
    items: [],
    // #83: past-due planned sessions. A separate list from `items`, because
    // these are in the PAST — see api/src/routes/activities.ts.
    needs_reflection: [],
    summary: { this_week: 0, matches: 0, training: 0, development: 0, needs_reflection: 0 },
  });
  // Coach roster (issue #74) — the coach view is built entirely from this
  // one existing endpoint (GET /players already scopes to the coach's
  // active coach_links roster, api/src/routes/players.ts:116-130). No new
  // endpoint invented; per-player upcoming activity / stats are omitted
  // rather than faked, since /activities/upcoming and /stats/summary both
  // require an explicit player_id for a coach (api/src/routes/authz.ts
  // resolveScope) — aggregating those across a roster would mean N calls
  // per dashboard load, which isn't "cheap" per the issue's own bar.
  const [coachPlayers, setCoachPlayers] = useState([]);
  // Admin feedback list (issue #226 slice 1) — GET /feedback, admin-only.
  const [adminFeedback, setAdminFeedback] = useState([]);
  // Issue #271: which of the player-scoped loads below came back as
  // "we don't know" rather than "here is the answer". Keyed per leg
  // (goals / matches / player / upcoming / notes) so a single flaky
  // request degrades exactly one card and nothing else. `reloadKey` /
  // `summaryReloadKey` are what the per-card Try-again buttons bump.
  const [failed, setFailed] = useState({});
  // Issue #762 — `computeSeasonRecordGate`'s own qualifying signal needs to
  // know "has the account's TRUE (unfiltered) match count arrived yet",
  // which `matches` alone cannot answer (it starts at `[]`, indistinguishable
  // from "genuinely zero matches"). Set once, inside the SAME `allSettled`
  // block below, the instant that leg has resolved (fulfilled OR rejected —
  // a coach/admin session skips the whole effect and never sets this, which
  // is fine: `showSeasonRecord` is unreachable on either persona already).
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [summaryReloadKey, setSummaryReloadKey] = useState(0);
  // Filter selection persists per page (issue #55) — mirrors the
  // nw_perf_* localStorage key pattern from PerformanceTracking.jsx.
  const [season, setSeason] = useState(() => localStorage.getItem("nw_dash_season") ?? "all");
  const [comp, setComp] = useState(() => localStorage.getItem("nw_dash_comp") ?? "all");
  // Issue #78: team filter, same persistence pattern.
  const [team, setTeam] = useState(() => localStorage.getItem("nw_dash_team") ?? "all");
  // The three option lists live in one hook, shared with the Dashboard
  // screen (#1147). Coach and admin have no player profile: no request.
  const { seasonOptions, compOptions, teamOptions } = useMatchFilterOptions({
    enabled: !isCoach && !isAdmin,
    reloadKey,
  });

  useEffect(() => { try { localStorage.setItem("nw_dash_season", season); } catch { /* quota / private mode */ } }, [season]);
  useEffect(() => { try { localStorage.setItem("nw_dash_comp", comp); } catch { /* quota / private mode */ } }, [comp]);
  useEffect(() => { try { localStorage.setItem("nw_dash_team", team); } catch { /* quota / private mode */ } }, [team]);

  useEffect(() => {
    // Issue #74 (coach) / #226 (admin): these are all player-scoped
    // endpoints (default to the caller's own profile — see resolveScope)
    // with no meaning for a coach or admin caller — admin has no player
    // profile at all, so `/players/me` etc. would 400/404 — skip them
    // entirely for both roles rather than let them error.
    if (isCoach || isAdmin) return undefined;
    let cancelled = false;
    (async () => {
      // Issue #271: `Promise.all` + one outer catch meant a single failed
      // leg discarded the four that succeeded, and the two legs that had
      // their own `.catch(() => ({ data: [] }))` substituted an EMPTY
      // ANSWER for a missing one — which this page then renders as "No
      // active goals yet" / "No upcoming activities scheduled". Those are
      // claims about the player's data, asserted about data we never
      // received. `allSettled` + a per-leg failure flag keeps every leg
      // that did arrive and lets the render tell "you have none" apart
      // from "we don't know".
      const [g, m, p, u, n] = await Promise.allSettled([
        getWithRetry("/goals"),
        getWithRetry("/matches"),
        getWithRetry("/players/me"),
        getWithRetry("/activities/upcoming?days=60", { headers: clientDateHeaders() }),
        getWithRetry("/notes"),
      ]);
      if (cancelled) return;
      // A 404 from /players/me is the documented "no profile row yet"
      // answer, not a failure — treat it as an absent player, exactly as
      // the previous `.catch(() => ({ data: null }))` did, and do NOT flag
      // it as a load failure.
      const playerMissing = p.status === "rejected" && p.reason?.response?.status === 404;
      const nextFailed = {
        goals: g.status === "rejected",
        matches: m.status === "rejected",
        player: p.status === "rejected" && !playerMissing,
        upcoming: u.status === "rejected",
        notes: n.status === "rejected",
      };
      if (g.status === "fulfilled") setGoals(g.value.data || []);
      if (m.status === "fulfilled") setMatches(m.value.data || []);
      if (p.status === "fulfilled") setPlayer(p.value.data);
      else if (playerMissing) setPlayer(null);
      if (u.status === "fulfilled") setUpcoming(u.value.data || { items: [], summary: {} });
      if (n.status === "fulfilled") setNotes(n.value.data || []);
      setFailed(nextFailed);
      setMatchesLoaded(true);
      for (const [key, r] of [["goals", g], ["matches", m], ["player", p], ["upcoming", u], ["notes", n]]) {
        if (nextFailed[key]) console.error(`Dashboard: failed to load ${key}`, r.reason);
      }
    })();
    return () => { cancelled = true; };
  }, [isCoach, isAdmin, reloadKey]);

  useEffect(() => {
    if (isCoach || isAdmin) return undefined;
    let cancelled = false;
    // Issue #271, THE proven site. Was:
    //   api.get(...).then((r) => setSummary(r.data)).catch(() => {});
    // with the render doing `summary?.total_runs ?? 0` — so one 503 gave a
    // player who had just logged an innings a permanent, silent
    // "Total Runs 0", and (fetch-on-mount only) it never recovered. Caught
    // live during #265: journey 5's own API call returned total_runs = 17
    // on the line immediately before the dashboard showed 0 for a full 15s.
    //
    // Note `setSummary(null)` on failure rather than leaving the previous
    // filter's numbers up: totals labelled with a filter they were not
    // computed for are the same lie in a different costume.
    getWithRetry(`/stats/summary${toQuery(season, comp, team)}`)
      .then((r) => { if (!cancelled) { setSummary(r.data); setSummaryFailed(false); } })
      .catch((e) => {
        if (cancelled) return;
        setSummary(null);
        setSummaryFailed(true);
        console.error("Dashboard: failed to load /stats/summary", e);
      });
    return () => { cancelled = true; };
  }, [season, comp, team, isCoach, isAdmin, summaryReloadKey]);

  // Coach roster fetch (issue #74) — `[isCoach]` (rather than `[]`) so
  // switching persona via the demo role banner (Layout.jsx switchDemoRole)
  // re-fetches immediately instead of leaving a stale/empty roster.
  useEffect(() => {
    if (!isCoach) return;
    api.get("/players").then((r) => setCoachPlayers(r.data || [])).catch(() => setCoachPlayers([]));
  }, [isCoach]);

  // Admin feedback fetch (issue #226 slice 1): the minimal admin surface
  // — GET /feedback is admin-only (feedback.ts) and, until this slice,
  // nobody could ever hold the role that reads it (issue #226's whole
  // premise). `[isAdmin]`, same reasoning as the coach roster fetch above.
  useEffect(() => {
    if (!isAdmin) return;
    api.get("/feedback").then((r) => setAdminFeedback(r.data || [])).catch(() => setAdminFeedback([]));
  }, [isAdmin]);

  // Issue #271: `null`, not `0`, when the profile has not arrived. "0%
  // complete" next to a full progress track is a plausible real value — it
  // is what a brand-new player genuinely sees — so a failed /players/me
  // used to tell an established player their profile was empty.
  const completion = (() => {
    if (failed.player) return null;
    if (!player) return 0;
    const fields = ["full_name", "club", "country", "batting_style", "bowling_style", "playing_role", "date_of_birth", "school"];
    const filled = fields.filter((f) => player?.[f]).length;
    return Math.round((filled / fields.length) * 100);
  })();

  const recent = matches.slice(-3).reverse();
  // Issue #193: "active" is a legacy status string — the live vocabulary
  // (DevelopmentPlan.jsx normalizeStatus / CoachPortal.jsx) is not_started /
  // in_progress / completed, plus the pre-rename "active" that can still
  // exist on old rows. Matching on the literal "active" filtered out every
  // goal a player actually has in progress. Match DevelopmentPlan.jsx:121's
  // reading instead — "active" means "not yet completed" — since this card
  // links straight to that page and a card that disagrees with the page it
  // links to reads as a second bug.
  const activeGoals = goals.filter((g) => g.status !== "completed");
  // #83: past-due planned training sessions. The API's count is preferred
  // over the array's length so the two can never disagree; the fallback
  // covers a response cached before this field existed.
  const needsReflectionCount =
    upcoming.summary?.needs_reflection ?? (upcoming.needs_reflection?.length || 0);
  const filterActive = season !== "all" || comp !== "all" || team !== "all";
  const headingSuffix = [season !== "all" ? season : null, comp !== "all" ? comp : null, team !== "all" ? team : null]
    .filter(Boolean)
    .join(" · ");
  // Issue #760 / #702's own gate defect — the pure formula lives in
  // `lib/recordGate.js` (unit-tested directly there); see that file's own
  // header for the full reasoning on why the flag still gates the fix and
  // what each of the two booleans below means.
  const recordScreenOn = newUi("record");
  const { isEmpty, showEmptyRecord, recordLoading, showRecordOne } = computeRecordGate({
    recordScreenOn,
    summary,
    filterActive,
    summaryFailed,
  });

  // Issue #762 — the season record. A SEPARATE screen id from `record`
  // above (`lib/featureFlags.js`'s `NEW_UI_SCREENS`), so it is gated by its
  // own pure function rather than a fifth `computeRecordGate` outcome — see
  // `computeSeasonRecordGate`'s own header for why the signal is
  // `matches.length` (always unfiltered) rather than `summary.matches`.
  // `matches.length >= 2` and `showRecordOne`'s `summary.matches === 1` (or
  // `showEmptyRecord`'s `matches === 0`) are mutually exclusive by
  // construction — no ordering conflict between the two gates below.
  // D149 (PR #880 review) — `season-record` turns on only when `record`
  // is also on; `recordScreenOn` is passed straight through, no second
  // `newUi("record")` read. `seasonRecordOn` below stays the RAW flag
  // (used by `renderSnapshotFilters` too, ANDed the same way there) —
  // `computeSeasonRecordGate` does its own ANDing internally so the gate
  // logic lives in exactly one place.
  const seasonRecordOn = newUi("season-record");
  const seasonRecordActuallyOn = seasonRecordOn && recordScreenOn;
  const { seasonRecordLoading, showSeasonRecord } = computeSeasonRecordGate({
    seasonRecordOn,
    recordScreenOn,
    matchesTotal: matches.length,
    matchesLoaded,
    matchesFailed: failed.matches,
  });
  // Issue #1217 — the record's own failed-to-load states (0 or 1 matches
  // while `/stats/summary` is down); see `computeRecordFailedGate`'s header.
  const { recordFailedEmpty, recordFailedOne } = computeRecordFailedGate({
    recordScreenOn,
    filterActive,
    summaryFailed,
    matchesLoaded,
    matchesFailed: failed.matches,
    matchesTotal: matches.length,
  });

  // Issue #1073 / #762 ruling 4 (D213.4). On the new look a player with
  // fewer than 2 matches has no filter control at all (`renderSnapshotFilters`
  // returns null, and the season record owns filtering from 2 matches), so a
  // `nw_dash_*` value still in localStorage is stale by definition: it hides
  // the record-empty / record-one screens behind `filterActive` and strands
  // the player on the legacy "Nothing matches this filter" sentence with
  // nothing to clear it. Reset silently; the persistence effects above write
  // "all" back. Guarded by `seasonRecordActuallyOn`, so the flag-off leg is
  // untouched. A failed GET /matches leaves `matches` at [] with the count
  // UNKNOWN, not below two, so it never clears (the season record's own R1
  // shows it on `matchesFailed`). Admin/coach never reach `matchesLoaded` (the load effect skips).
  useEffect(() => {
    if (seasonRecordActuallyOn && matchesLoaded && !failed.matches && matches.length < 2 && filterActive) {
      setSeason("all");
      setComp("all");
      setTeam("all");
    }
  }, [seasonRecordActuallyOn, matchesLoaded, failed.matches, matches.length, filterActive]);

  // Issue #74: coach persona gets its own view entirely — no "Log a match" /
  // "Log training" hero buttons, no empty-state prompts to create anything,
  // and no player summary/goals/analytics (that would just be showing one
  // arbitrary linked player's private data). All hooks above have already
  // run unconditionally, so branching the JSX here is safe.
  //
  // #384 item 3 makes this branch unreachable in practice — a coach cannot
  // open /dashboard any more (App.js) — and it stays for exactly that
  // reason: hidden, not deleted. See CoachDashboard's own note below.
  if (isCoach) {
    return <CoachDashboard user={user} players={coachPlayers} />;
  }

  // Issue #226 slice 1: admin's own view, same branching pattern as coach
  // above — no player summary/goals/analytics, and (the point of this
  // slice) somewhere to actually read the feedback list that
  // `GET /feedback` has been serving with nobody able to reach it.
  if (isAdmin) {
    return <AdminDashboard user={user} feedback={adminFeedback} />;
  }

  // Issues #760/#761/#762, one combined early return. `recordLoading` (B3),
  // `showEmptyRecord` (#760), `showRecordOne` (#761), and the season
  // record's own loading/settled pair (#762) used to each get their own
  // `if` inline; #762 adding a fifth and sixth decision point here would
  // have pushed this function's own cyclomatic complexity past the 65
  // budget `eslint.config.mjs` fixes for it by name (it is the file the
  // budget is calibrated against — see that file's own header). Moved into
  // `renderRecordScreens` below instead, the same discipline
  // `renderRecordOne` already established for `showRecordOne` alone:
  // `null` there means "none of the five applies", and this function falls
  // through to the ordinary populated dashboard exactly as it did before
  // any of these three issues existed.
  const recordScreen = renderRecordScreens({
    recordLoading,
    showEmptyRecord,
    showRecordOne,
    seasonRecordLoading,
    showSeasonRecord,
    recordFailedEmpty,
    recordFailedOne,
    summary,
    matches,
    matchesFailed: failed.matches,
    onRetryRecentMatches: () => setReloadKey((k) => k + 1),
    onRetrySummary: () => setSummaryReloadKey((k) => k + 1),
    season,
    comp,
    team,
    setSeason,
    setComp,
    setTeam,
    seasonOptions,
    // Issue #762 — a delete bumps BOTH reload keys, never just `matches`'s
    // own. `showRecordOne`/`showEmptyRecord` read `summary.matches`, and
    // `showSeasonRecord` reads `matches.length` — two independent fetches,
    // each on its own effect (`[…, reloadKey]` vs `[…, summaryReloadKey]`).
    // Bumping only `reloadKey` left `summary.matches` stale at the OLD
    // count for one extra render after a delete dropped the account from
    // two matches to one: `matches.length` (1) failed `showSeasonRecord`'s
    // own `>= 2` floor while `summary.matches` (still 2) failed
    // `showRecordOne`'s `=== 1` — neither gate fired, and the account fell
    // all the way through to the shipped, unflagged populated dashboard
    // for a frame (caught live by journey 37's own delete-to-one-match
    // step, which found the OLD sidebar chrome on screen instead of
    // RecordOneMatchScreen).
    onMatchDeleted: () => {
      setReloadKey((k) => k + 1);
      setSummaryReloadKey((k) => k + 1);
    },
  });
  if (recordScreen) return recordScreen;

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Issue #289: the other half of "Skip for now". A player who skipped
          the first-run walkthrough lands here, and this is the way back —
          a prompt, not a block and not a nag. It renders only while the
          server says so (`show_resume_prompt`), which means: they have been
          asked and answered, they still have steps outstanding, AND their
          account is still empty. Log a match or set a goal and it is gone
          for good, because at that point they are using the app on their own
          terms and a card telling them to go back and finish a form has
          stopped being help.

          Deliberately full-width and OUTSIDE the bento grid below, for the
          reason #271's failure banner records: new content inside an
          existing grid cell is what detonates #251's phone-390 overflow.

          Issue #760: unreachable while `showEmptyRecord` is true (that
          branch returns above and renders its own copy of this same
          component) — kept here, unchanged, for the filtered-empty and
          populated cases, and for the whole flag-off leg. */}
      <OnboardingResumePrompt />

      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
        <div className="absolute inset-0 bg-grid-slate opacity-20" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }} data-testid="dashboard-heading">
              Career Dashboard{headingSuffix && <span className="text-white/70"> · {headingSuffix}</span>}
            </p>
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">
              Welcome back, {user?.display_name?.split(" ")[0] || "Player"}
            </h1>
            {/* Issue #188: the player surface's one brand line, and the only
                one it gets. The eyebrow above stays "Career Dashboard" — it
                composes with the filter suffix, so a slogan there would read
                "Every number has a direction · 2024/25 · T20". Here it sits
                directly above the numbers it is a claim about. */}
            <p className="text-white/70 mt-2 max-w-xl">Every number has a direction.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/performance"><Button data-testid="dash-log-match" style={{ background: "#D4A017" }} className="hover:opacity-90">Log a match</Button></Link>
            <Link to="/learn?tab=training"><Button data-testid="dash-log-training" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">Log training</Button></Link>
            {AI_COACH_ENABLED && (
              <Link to="/coaching-insights"><Button data-testid="dash-ask-ai" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">Ask Coach AI</Button></Link>
            )}
          </div>
        </div>
      </div>

      {/* Snapshot filters — issue #762 ruling 4: this is the "second
          filter bar" the season record absorbs (`SeasonRecordScreen.jsx`'s
          own three boxes, fed by the SAME season/comp/team state), so it is
          skipped outright once the season record is ACTUALLY on — not
          only while `showSeasonRecord` is true above (that early return
          never reaches here at all), but also for the narrow matches<2
          edge this branch can still be reached on. D149 (PR #880 review)
          — `season-record` alone, `record` off, is exactly today's
          flag-off behaviour, so this row stays visible in that single-flag
          combination now (it did not before D149). Pulled into
          `renderSnapshotFilters` (same complexity-budget discipline as
          `renderRecordOne`/`renderSeasonRecord` above) so the on/off
          decision does not cost this function a branch of its own. */}
      {renderSnapshotFilters({ seasonRecordOn: seasonRecordActuallyOn, comp, season, team, setComp, setSeason, setTeam, compOptions, seasonOptions, teamOptions })}

      {isEmpty ? (
        // Issue #760 § 1: behind `newUi("record")` this is reachable only
        // with a filter active — a brand-new, unfiltered account takes the
        // `showEmptyRecord` branch above instead — so the copy is named
        // explicitly as the filter's doing once the corrected gate can
        // actually tell the two apart. The flag-off leg keeps the exact old
        // wording: its gate already implied a filter (the old, un-fixed
        // formula), so nothing here needed to change for it.
        <Card className="border-slate-200" data-testid="dashboard-empty">
          <CardContent className="p-12 text-center">
            <p className="text-slate-500">
              {recordScreenOn ? (
                // D110 (overlay decisions, 738bd0c) — the fixed sentence,
                // not the old interpolated "any season in any competition"
                // (which for the common single-filter case read as almost
                // the SAME words #760's own empty-record title uses, and
                // no longer names the filter's own value — "clear it" is
                // the actionable instruction, not a echo of what was
                // typed). The flag-off leg is untouched by this decision:
                // its own wording (below) still names season/comp/team.
                "Nothing matches this filter. Clear it to see your whole record."
              ) : (
                <>
                  No matches for {season === "all" ? "any season" : season} in {comp === "all" ? "any competition" : comp}
                  {team !== "all" && <> for {team}</>}
                </>
              )}
            </p>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Issue #271: the failure banner for the four tiles below. It sits
          OUTSIDE the bento grid on purpose — new content inside an existing
          grid cell is what detonates #251's phone-390 overflow, and this
          card is full-width with wrapping text and a `min-w-0` flex child. */}
      {summaryFailed && (
        <Card className="border-slate-300 bg-slate-50" data-testid="dash-summary-error">
          <CardContent className="p-4 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 break-words">Your career totals didn't load</p>
              <p className="text-xs text-slate-600 mt-0.5 break-words">
                Nothing has been lost — we couldn't reach the server, so the tiles below show a dash instead of a number.
              </p>
            </div>
            <Button size="sm" variant="outline" className="h-8 text-xs" data-testid="dash-summary-retry"
              onClick={() => setSummaryReloadKey((k) => k + 1)}>
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bento grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Issue #271: `— `, never `?? 0`. A zero here is a LEGITIMATE value
            (a player with no runs), so it is indistinguishable from success
            — which is exactly why it was the worst available thing to show
            when the fetch had failed. The dash is unmistakably not a score.
            This also covers the pre-load frame, which flashed a 0 for the
            same reason. */}
        <StatCard title="Total Runs" value={summary ? summary.total_runs : "—"} icon={TrendingUp} color="#0B2E59" tid="stat-runs" />
        <StatCard title="Batting Avg" value={summary ? avgText(summary.avg) : "—"} icon={Activity} color="#D4A017" tid="stat-avg" />
        <StatCard title="Wickets" value={summary ? summary.total_wickets : "—"} icon={Trophy} color="#84C5FF" tid="stat-wickets" />
        <StatCard title="Matches" value={summary ? summary.matches : "—"} icon={Calendar} color="#0B2E59" tid="stat-matches" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Runs trend */}
        <Card className="lg:col-span-2 border-slate-200" data-testid="dash-trend">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Recent form</p>
                {/* Issue #271: "last 0 matches" over an empty chart is a
                    claim; "last — matches" is an admission. */}
                <h3 className="font-display text-xl font-bold text-slate-900">Runs across last {summary ? (summary.trend?.length || 0) : "—"} matches</h3>
              </div>
              {/* Issue #762 — braced (`to={"/statistics"}`), not
                  `to="/statistics"`: an unbraced attribute now fires BOTH
                  flagSafeLinks.test.js's generic-literal pass and its
                  to=/href= attribute pass on the same line, which no single
                  allowlist entry can cover under that file's own "exactly
                  one finding per entry" rule. Same value, same behaviour —
                  see that file's own ALLOWLIST entry for this link. */}
              <Link to={"/statistics"} className="text-xs font-semibold text-[#0B2E59] flex items-center gap-1">View all <ArrowRight size={12} /></Link>
            </div>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={summary?.trend || []}>
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                  <Line type="monotone" dataKey="runs" stroke="#0B2E59" strokeWidth={2.5} dot={{ fill: "#D4A017", r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Profile completion */}
        <Card className="border-slate-200" data-testid="dash-profile">
          <CardContent className="p-6">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Profile completion</p>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="font-display font-black text-4xl text-slate-900">{completion == null ? "—" : `${completion}%`}</span>
              <span className="text-xs text-slate-500">complete</span>
            </div>
            <Progress value={completion ?? 0} className="mt-3 h-2" />
            <p className="text-sm text-slate-600 mt-4">
              {completion == null
                ? "We couldn't reach the server to check your profile — nothing has been lost."
                : "A complete profile helps coaches discover you and unlock better recommendations."}
            </p>
            {/* #338: `/profile` is a redirect now, and landing at the top
                of a long Settings page is not "complete profile". The hash
                names the section the editor lives in and ScrollToTop
                honours it, so this button still opens on the form. */}
            <Link to="/settings#player-profile"><Button variant="outline" className="mt-4 w-full" data-testid="dash-complete-profile">Complete profile <ArrowRight size={14} className="ml-1" /></Button></Link>
          </CardContent>
        </Card>
      </div>
      </>
      )}

      {/* Goals + upcoming + coach feedback (issue #184: third card added to
          this existing row per Martin's PR #192 answer 3 — peer content, a
          coach-relationship signal, not a new category worth its own row.
          Goals dropped from lg:col-span-2 to a plain grid cell so all three
          land 3-across on desktop and stack on mobile, matching every other
          Dashboard card's responsive pattern.) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-slate-200" data-testid="dash-goals">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Active goals</p>
                <h3 className="font-display text-xl font-bold text-slate-900">Development plan</h3>
              </div>
              <Link to="/development" className="text-xs font-semibold text-[#0B2E59] flex items-center gap-1">Manage <ArrowRight size={12} /></Link>
            </div>
            <div className="space-y-4">
              {/* Issue #271: "No active goals yet" is a claim about the
                  player's data — it must never be asserted about data we
                  never received. */}
              {failed.goals && (
                <LoadFailureNote what="your goals" testId="dash-goals-error" onRetry={() => setReloadKey((k) => k + 1)} />
              )}
              {!failed.goals && activeGoals.length === 0 && <p className="text-sm text-slate-500">No active goals yet. Create one to start your journey.</p>}
              {activeGoals.slice(0, 3).map((g) => (
                <div key={g.goal_id} className="p-4 rounded-xl border border-slate-200 hover:border-[#84C5FF] transition-all">
                  <div className="flex items-start justify-between mb-2 gap-3">
                    <div>
                      <div className="font-semibold text-slate-900">{g.goal_title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">Due {g.target_date}</div>
                    </div>
                    <Badge style={{ background: "#84C5FF", color: "#0B2E59" }}>{g.progress_percentage}%</Badge>
                  </div>
                  <Progress value={g.progress_percentage} className="h-1.5" />
                </div>
              ))}
              {/* Issue #193/#197: this card only ever showed the first 3 —
                  harmless while "active" was mis-filtered out, but once that
                  read bug was fixed a player with more than 3 real
                  in-progress goals silently loses the rest with no signal
                  they exist. Only shown past the cap, so the common case
                  (<=3 goals) is unchanged. */}
              {activeGoals.length > 3 && (
                <p className="text-xs text-slate-500 text-center" data-testid="dash-goals-overflow">
                  Showing 3 of {activeGoals.length}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        <CoachFeedbackTile
          player={player}
          notes={notes}
          failed={failed.player || failed.notes}
          onRetry={() => setReloadKey((k) => k + 1)}
        />

        <Card className="border-slate-200 flex flex-col" data-testid="dash-upcoming">
          <CardContent className="p-6 flex flex-col flex-1">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Upcoming</p>
                <h3 className="font-display text-xl font-bold text-slate-900">Activities</h3>
              </div>
              {/* Issue #79: was a single "Schedule" link to /performance —
                  the one destination this widget offered, whichever of the
                  three kinds of thing you came to add. UpcomingAddMenu.jsx
                  owns what it offers now and to whom; the rows below are
                  deliberately untouched (ticket point 3). */}
              <UpcomingAddMenu />
            </div>

            {/* Weekly summary chips — issue #271: a dash when the fetch
                failed, because "This week 0" is a real schedule, not an
                error. */}
            <div className="grid grid-cols-4 gap-2 mb-4" data-testid="upcoming-summary">
              <MiniCount label="This week" value={failed.upcoming ? "—" : (upcoming.summary?.this_week || 0)} color="#0B2E59" />
              <MiniCount label="Matches" value={failed.upcoming ? "—" : (upcoming.summary?.matches || 0)} color="#D4A017" />
              <MiniCount label="Training" value={failed.upcoming ? "—" : (upcoming.summary?.training || 0)} color="#84C5FF" />
              <MiniCount label="Dev" value={failed.upcoming ? "—" : (upcoming.summary?.development || 0)} color="#10B981" />
            </div>

            {/* Issue #83: the completion prompt, and this is the "where it
                will actually be seen" the ticket asks for. Above the list
                rather than in it — the list is soonest-first and these are
                overdue, so they would sort to the bottom of the one widget
                that is meant to surface them. Renders nothing at all when
                the count is 0, so a player with nothing outstanding sees
                the card exactly as before. */}
            {!failed.upcoming && needsReflectionCount > 0 && (
              <Link
                to="/learn?tab=training"
                className="flex items-center gap-2 rounded-lg border p-3 mb-3 hover:shadow-sm transition-all"
                style={{ borderColor: "#D4A01755", background: "#D4A01712" }}
                data-testid="dash-needs-reflection"
              >
                <ClipboardList size={16} color="#D4A017" className="shrink-0" />
                <span className="text-xs text-slate-700 min-w-0">
                  <b data-testid="dash-needs-reflection-count">{needsReflectionCount}</b>
                  {needsReflectionCount === 1
                    ? " training session has been and gone — add how it went."
                    : " training sessions have been and gone — add how they went."}
                </span>
                <ArrowRight size={12} className="ml-auto shrink-0" color="#D4A017" />
              </Link>
            )}

            {failed.upcoming ? (
              <LoadFailureNote what="your upcoming activities" testId="upcoming-error" onRetry={() => setReloadKey((k) => k + 1)} />
            ) : upcoming.items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid="upcoming-empty">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#0B2E5910" }}>
                  <AlertCircle size={20} color="#0B2E59" />
                </div>
                <p className="text-sm font-semibold text-slate-900">No upcoming activities scheduled.</p>
                <p className="text-xs text-slate-500 mt-1 max-w-[240px]">Create a training session, match, goal review, or development task to see it here.</p>
                <div className="flex gap-1.5 mt-3">
                  <Link to="/performance"><Button size="sm" variant="outline" className="h-8 text-xs">Log match</Button></Link>
                  <Link to="/learn?tab=training"><Button size="sm" variant="outline" className="h-8 text-xs">Log training</Button></Link>
                </div>
              </div>
            ) : (
              <div className="space-y-2 overflow-y-auto pr-1 -mr-1" style={{ maxHeight: 460 }} data-testid="upcoming-list">
                {upcoming.items.slice(0, 25).map((a) => (
                  <ActivityRow key={`${a.kind}-${a.id}`} a={a} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quick nav */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Quick navigation</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {NAV_CARDS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={`quicknav-${c.label.toLowerCase()}`}>
              <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: c.color }}>
                    <c.icon size={20} color="#fff" />
                  </div>
                  <div className="mt-3 font-semibold text-slate-900">{c.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">Open <ArrowRight size={10} /></div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent matches */}
      <Card className="border-slate-200" data-testid="dash-recent-matches">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Recent performance</p>
              <h3 className="font-display text-xl font-bold text-slate-900">Last 3 matches</h3>
            </div>
            <Link to="/performance" className="text-xs font-semibold text-[#0B2E59] flex items-center gap-1">All matches <ArrowRight size={12} /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {recent.map((m) => (
              <div key={m.match_id} className="p-4 rounded-xl border border-slate-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500">{m.competition}</div>
                  <Badge variant="outline" className="text-[10px]">{dateTimeLabel(m.match_date, m.match_time)}</Badge>
                </div>
                <div className="font-semibold text-slate-900">vs {m.opponent}</div>
                <div className="text-xs text-slate-500 mb-3">{m.ground}</div>
                {/* #715: a counter that was never recorded is `null` now, and a
                    raw `{m.batting?.runs}` renders nothing at all — a labelled
                    tile with no value in it, which reads as broken rather
                    than as "not recorded". `counterText` prints the same dash
                    the summary cards above use (#271): a dash, never a zero,
                    and never an empty box either. */}
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="p-2 rounded-lg bg-slate-50">
                    <div className="text-[10px] uppercase text-slate-500">Runs</div>
                    <div className="font-display font-bold text-lg text-[#0B2E59]">{counterText(m.batting?.runs)}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-50">
                    <div className="text-[10px] uppercase text-slate-500">Wkts</div>
                    <div className="font-display font-bold text-lg text-[#D4A017]">{counterText(m.bowling?.wickets)}</div>
                  </div>
                  <div className="p-2 rounded-lg bg-slate-50">
                    <div className="text-[10px] uppercase text-slate-500">SR</div>
                    <div className="font-display font-bold text-lg text-slate-700">{counterText(m.batting?.strike_rate)}</div>
                  </div>
                </div>
              </div>
            ))}
            {/* Issue #188: an empty state is the one screen where a player
                has no evidence they are progressing, so it says what the
                screen will hold rather than issuing a to-do. `col-span-full`
                not `col-span-3`: at phone-390 the parent is `grid-cols-1`,
                where spanning 3 tracks invents two auto-sized implicit
                columns that this longer string can push past the viewport
                (the #251 class). At md+ the two are identical. */}
            {/* Issue #271: "No matches logged yet" told a player who had
                just logged one that it was gone. `col-span-full` for the
                same #251 reason as the empty state below it. */}
            {failed.matches && (
              <div className="col-span-full min-w-0">
                <LoadFailureNote what="your recent matches" testId="dash-recent-matches-error" onRetry={() => setReloadKey((k) => k + 1)} />
              </div>
            )}
            {!failed.matches && recent.length === 0 && <div className="text-sm text-slate-500 col-span-full">No matches logged yet — your record starts with the first one.</div>}
          </div>
        </CardContent>
      </Card>

      {/* -------- Deep career analytics (Batting · Bowling · WK · Development) -------- */}
      <div className="pt-4 border-t border-slate-200">
        <p className="text-[11px] uppercase tracking-[0.3em] text-slate-500 mb-3">Deep analytics</p>
        <CareerDashboard hideHeader season={season} competition={comp} team={team} />
      </div>
    </div>
  );
}

// ---------------- Coach Feedback tile (issue #184) ----------------
// Third card in the Goals + Upcoming row (Martin's PR #192 answer 3).
// `player` is null while /players/me is still in flight (same pattern
// DevelopmentPlan.jsx's CoachFeedbackPanel uses for `hasCoach`) — render
// nothing rather than flash the wrong one of the two empty states below.
// Deliberately no create/edit affordance here: this tile only ever reads
// `notes` + `player.coach_feedback_seen_at`, both already fetched for the
// page, and both already reachable to a player for exactly their own
// profile (no cross-player leak possible — see api/src/routes/notes.ts /
// players.ts, resolveScope).
function CoachFeedbackTile({ player, notes, failed, onRetry }) {
  // Issue #271: `/players/me` or `/notes` failing used to fall through to
  // `player = null` (tile absent) or `notes = []` ("Your coach hasn't left
  // feedback yet") — the second of which is a statement about the coach,
  // made on no evidence. Failure is now its own state, and it is shown
  // rather than guessed at.
  if (failed) {
    return (
      <Card className="border-slate-200 flex flex-col" data-testid="dash-feedback">
        <CardContent className="p-6 flex flex-col flex-1">
          <div className="mb-3">
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Coach feedback</p>
            <h3 className="font-display text-xl font-bold text-slate-900">My Development</h3>
          </div>
          <LoadFailureNote what="coach feedback" testId="dash-feedback-error" onRetry={onRetry} />
        </CardContent>
      </Card>
    );
  }
  if (!player) return null;

  const hasCoach = !!player.has_coach;
  const seenAt = player.coach_feedback_seen_at;
  // No backfill on rollout (Martin's PR #192 answer 2): `seenAt == null`
  // means "everything is unread", not "nothing is" — every existing pilot
  // tester sees a one-time "N new" on first load after deploy, matching
  // spec §3.4 item 2's accepted rollout behaviour.
  const sorted = [...notes].sort((a, b) => (b.created_date || "").localeCompare(a.created_date || ""));
  const latest = sorted[0];
  const unreadCount = seenAt == null ? notes.length : notes.filter((n) => n.created_date > seenAt).length;

  return (
    <Card className="border-slate-200 flex flex-col" data-testid="dash-feedback">
      <CardContent className="p-6 flex flex-col flex-1">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Coach feedback</p>
            <h3 className="font-display text-xl font-bold text-slate-900">My Development</h3>
          </div>
          <Link
            to="/development?tab=feedback"
            className="text-xs font-semibold text-[#0B2E59] flex items-center gap-1"
            data-testid="dash-feedback-view-all"
          >
            View <ArrowRight size={12} />
          </Link>
        </div>

        {!hasCoach ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid="dash-feedback-empty-no-coach">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#64748B15" }}>
              <Users size={20} color="#64748B" />
            </div>
            <p className="text-sm font-semibold text-slate-900">You don't have a coach yet</p>
            <p className="text-xs text-slate-500 mt-1 max-w-[220px]">Once a coach links to your profile, their feedback will show up here.</p>
          </div>
        ) : !latest ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center py-8" data-testid="dash-feedback-empty-no-notes">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3" style={{ background: "#D4A01720" }}>
              <NotebookPen size={20} color="#D4A017" />
            </div>
            <p className="text-sm font-semibold text-slate-900">No coach feedback yet</p>
            <p className="text-xs text-slate-500 mt-1 max-w-[220px]">Check back after your next session.</p>
          </div>
        ) : (
          <div className="flex-1 flex flex-col" data-testid="dash-feedback-latest">
            {unreadCount > 0 && (
              <Badge className="self-start mb-2 text-[10px]" style={{ background: "#D4A017", color: "#0B2E59" }} data-testid="dash-feedback-unread">
                {unreadCount} new
              </Badge>
            )}
            <div className="p-4 rounded-xl border border-slate-200 flex-1">
              <p className="text-sm text-slate-800 line-clamp-3">{latest.feedback}</p>
              <div className="text-xs text-slate-500 mt-3">— {latest.coach_name || "Your coach"} · {latest.created_date?.slice(0, 10)}</div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Coach-only quick nav (issue #74) — originally mirrored the coach's
// sidebar (components/Layout.jsx COACH_NAV) minus Dashboard/Settings.
// Issue #222 dropped "Feedback & Goals" and "Grounds" from that sidebar
// (the first was a duplicate of "My Players" with a different tab
// preselected; the second doesn't belong at the coach top level — a coach
// reaches ground data through a linked player, #82). Both routes stay
// live, and these two dashboard tiles are exactly the "any existing link
// pointing at them must keep working" case #222 calls out — deep links,
// not sidebar entries, so they are deliberately kept here even though the
// sidebar no longer has a 1:1 counterpart for them.
const COACH_NAV_CARDS = [
  // Issue #819 (D39): label is null here on purpose — this card's name is
  // decided at render time by `coachPlayersName()`, below, never baked
  // into this module-level array (a module-scope read of the flag would
  // only ever run once, at import).
  { to: "/coach-portal", label: null, icon: Users, color: "#0B2E59", tid: "dash-coach-players" },
  { to: "/coach-portal?tab=feedback", label: "Feedback & Goals", icon: NotebookPen, color: "#D4A017", tid: "dash-coach-feedback" },
  { to: "/grounds-map", label: "Grounds", icon: MapPinned, color: "#84C5FF", tid: "dash-coach-grounds" },
];

// Coach dashboard (issue #74). Built from exactly one existing endpoint —
// GET /players, which for a coach caller already returns just their active
// coach_links roster (api/src/routes/players.ts) — plus a deep link into
// CoachPortal per player (CoachPortal.jsx reads the same `?player=` param
// to preselect, issue #74). Deliberately has no "Log a match" / "Log
// training" affordance anywhere, and no player-scoped stats/upcoming widget
// (those endpoints require a specific player_id for a coach caller, and
// fetching them per roster row wouldn't be "cheap" — omitted rather than
// faked, per the issue's own instruction).
/**
 * The coach's dashboard — UNREACHABLE SINCE #384 item 3, and deliberately
 * left standing.
 *
 * Andreas's decision of 2026-08-20 on Martin's feedback: hide, do not
 * delete. `/dashboard` is guarded to `["player","admin"]` in App.js and a
 * coach lands on My Players instead (`lib/homePath.js`), because this view
 * is thin enough to be noise until the coach side grows (#314, #383). The
 * moment it is worth landing on again, putting "coach" back in that array
 * is the whole reversal — which is only true while this component and the
 * `isCoach` branch above it stay exactly as they are. Do not tidy them away
 * as dead code: they are a decision that is expected to be revisited.
 */
function CoachDashboard({ user, players }) {
  return (
    <div className="space-y-8" data-testid="dashboard-coach">
      {/* Hero — no create actions */}
      <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
        <div className="absolute inset-0 bg-grid-slate opacity-20" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }} data-testid="dashboard-heading">
              Coaching Dashboard
            </p>
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">
              Welcome back, {user?.display_name?.split(" ")[0] || "Coach"}
            </h1>
            {/* Issue #188: the coach surface's one line, from the brand
                guide's audience table rather than the player-facing
                signature line — a coach's job here is the next step, not
                their own numbers. */}
            <p className="text-white/70 mt-2 max-w-xl">Know where each player stands, and plan the next step with purpose.</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/coach-portal"><Button data-testid="dash-coach-view-players" style={{ background: "#D4A017" }} className="hover:opacity-90">My players</Button></Link>
            <Link to="/coach-portal?tab=feedback"><Button data-testid="dash-coach-view-feedback" variant="outline" className="bg-white/10 border-white/30 text-white hover:bg-white/20">Feedback &amp; goals</Button></Link>
          </div>
        </div>
      </div>

      {/* Roster count */}
      <Card className="border-slate-200" data-testid="dash-roster-count">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Your roster</p>
            <div className="font-display font-black text-4xl mt-1 text-[#0B2E59]">{players.length}</div>
            <p className="text-sm text-slate-500 mt-1">{players.length === 1 ? "player linked to you" : "players linked to you"}</p>
          </div>
          <Users size={40} color="#0B2E59" />
        </CardContent>
      </Card>

      {/* Players */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Players</p>
        {players.length === 0 ? (
          <Card className="border-slate-200" data-testid="dashboard-empty">
            <CardContent className="p-12 text-center">
              {/* Issue #188: linking is player-initiated (Settings →
                  CoachAccessCard invites by email; the coach accepts in
                  CoachPortal), so a bare "none yet" left a coach with no
                  idea what would ever change it. */}
              <p className="text-slate-500">No players are linked to you yet — a player invites you by email, and you accept in the Coach Portal.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dash-roster">
            {players.map((p) => (
              <Link key={p.player_id} to={`/coach-portal?player=${p.player_id}`} data-testid={`dash-roster-${p.player_id}`}>
                <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full">
                  <CardContent className="p-5 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-white flex-shrink-0" style={{ background: "#0B2E59" }}>
                      {p.full_name?.[0] || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900 truncate">{p.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{p.playing_role || "—"}{p.club ? ` · ${p.club}` : ""}</div>
                    </div>
                    <ArrowRight size={14} className="text-slate-300 ml-auto flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      {/* Quick navigation */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Quick navigation</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {COACH_NAV_CARDS.map((c) => (
            <Link key={c.to} to={c.to} data-testid={c.tid}>
              <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer h-full">
                <CardContent className="p-5">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: c.color }}>
                    <c.icon size={20} color="#fff" />
                  </div>
                  <div className="mt-3 font-semibold text-slate-900">
                    {c.to === "/coach-portal" ? coachPlayersName() : c.label}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">Open <ArrowRight size={10} /></div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// Issue #226 slice 1: minimal admin surface, same "own view entirely"
// pattern as CoachDashboard above — not a full panel (see the issue: no
// provisioning UI, no club/tier model here, that's later slices). Just
// enough that an admin signing in has somewhere to land, and can read the
// feedback list `GET /feedback` (feedback.ts, admin-only) has been
// serving since issue #139 with nobody able to reach it.
const FEEDBACK_CATEGORY_LABEL = {
  function: "Function",
  ui_ux: "UI / UX",
  bug: "Bug",
  other: "Other",
};

function AdminDashboard({ user, feedback }) {
  const unfiledCount = feedback.filter((f) => !f.github_issue_url).length;

  return (
    <div className="space-y-8" data-testid="dashboard-admin">
      {/* Hero — no create actions, same reasoning as CoachDashboard's */}
      <div className="relative overflow-hidden rounded-2xl p-6 sm:p-8 text-white" style={{ background: "linear-gradient(135deg, #0B2E59 0%, #15417A 100%)" }}>
        <div className="absolute inset-0 bg-grid-slate opacity-20" />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "#D4A017" }} data-testid="dashboard-heading">
              Admin Dashboard
            </p>
            <h1 className="font-display font-black text-3xl sm:text-4xl mt-1">
              Welcome back, {user?.display_name || "Admin"}
            </h1>
            <p className="text-white/70 mt-2 max-w-xl">Set up accounts for players and coaches, and read the feedback they send in.</p>
          </div>
        </div>
      </div>

      {/* Accounts (issue #226) — the provisioning panel USED to render
          inline here, because slice 2 shipped before `/admin` existed and
          the dashboard was the only place an admin could reach. It now
          lives at `/admin/users` (Andreas, 2026-08-20: "one /admin area
          with sections"), so this is a way in rather than the surface
          itself: two doors to one screen is what the decision was against,
          but a dashboard with no route to the thing an admin is FOR is
          worse. `ADMIN_NAV`'s "Admin" entry is the primary door; this is
          the one on the page they land on. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Link to="/admin/users" data-testid="dash-admin-users-link">
          <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
            <CardContent className="p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
                <Users size={20} color="#0B2E59" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display font-bold text-slate-900">Accounts</div>
                <div className="text-xs text-slate-500 mt-0.5">Create players and coaches, and re-issue lost passwords.</div>
              </div>
              <ChevronRight size={16} className="text-slate-400 shrink-0" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Feedback count */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <Card className="border-slate-200" data-testid="dash-admin-feedback-count">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Feedback submissions</p>
              <div className="font-display font-black text-4xl mt-1 text-[#0B2E59]">{feedback.length}</div>
            </div>
            <NotebookPen size={40} color="#0B2E59" />
          </CardContent>
        </Card>
        <Card className="border-slate-200" data-testid="dash-admin-feedback-unfiled">
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Not filed to GitHub</p>
              <div className="font-display font-black text-4xl mt-1 text-[#0B2E59]">{unfiledCount}</div>
              <p className="text-sm text-slate-500 mt-1">Saved either way — filing is best-effort (issue #139).</p>
            </div>
            <AlertCircle size={40} color="#D4A017" />
          </CardContent>
        </Card>
      </div>

      {/* Feedback list */}
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500 mb-3">Feedback — newest first</p>
        {feedback.length === 0 ? (
          <Card className="border-slate-200" data-testid="dashboard-empty">
            <CardContent className="p-12 text-center">
              <p className="text-slate-500">No feedback has been submitted yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <Card key={f.id} className="border-slate-200" data-testid={`admin-feedback-${f.id}`}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge style={{ background: "#84C5FF33", color: "#0B2E59" }}>
                        {FEEDBACK_CATEGORY_LABEL[f.category] || f.category}
                      </Badge>
                      <Badge variant="outline" className="capitalize">{f.role}</Badge>
                      {f.route && <Badge variant="outline" className="text-[10px]">{f.route}</Badge>}
                    </div>
                    <span className="text-xs text-slate-500 whitespace-nowrap">
                      {f.created_at ? new Date(f.created_at).toLocaleString("en-NZ") : ""}
                    </span>
                  </div>
                  <p className="text-sm text-slate-800 mt-3 whitespace-pre-wrap">{f.message}</p>
                  <div className="mt-3 text-xs">
                    {f.github_issue_url ? (
                      <a
                        href={f.github_issue_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-semibold text-[#0B2E59] hover:underline"
                      >
                        Filed on GitHub &rarr;
                      </a>
                    ) : (
                      <span className="text-slate-400">Not filed to GitHub yet</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Issue #761 — the three states `showRecordOne` can resolve to, pulled out
 * of `Dashboard`'s own body (see that function's own comment on
 * `showRecordOne` for why): the separate `/matches` fetch this page makes
 * either failed, has not landed yet, or has — and only the last of those
 * carries the ONE match `RecordOneMatchScreen` needs for its hero line and
 * bowling card. `matches` is this page's FULL match list (not scoped to
 * "the one match on a one-match account" by any filter of its own); taking
 * the last element is safe only because `showRecordOne` is already true,
 * which means `summary.matches === 1` by construction — there is exactly
 * one to take.
 */
function renderRecordOne({ summary, matches, matchesFailed, onRetryRecentMatches, onMatchDeleted }) {
  if (matchesFailed) {
    return <RecordOneMatchScreen summary={summary} recentMatchesFailed onRetryRecentMatches={onRetryRecentMatches} />;
  }
  if (matches.length === 0) {
    return <RecordEmptyScreen loading />;
  }
  return <RecordOneMatchScreen summary={summary} match={matches[matches.length - 1]} onMatchDeleted={onMatchDeleted} />;
}

/**
 * Issues #760/#761/#762 — every early-return "this is not the ordinary
 * populated dashboard" decision, in ONE function. `Dashboard` itself calls
 * this exactly once and returns its result when non-null; `null` means
 * none of the five applies and `Dashboard` falls through to the ordinary
 * populated dashboard exactly as it did before any of these three issues
 * existed. See the call site's own comment for why this is one function
 * rather than five inline `if`s (the 65 complexity budget `eslint.
 * config.mjs` fixes BY NAME for this file).
 */
function renderRecordScreens({
  recordLoading,
  showEmptyRecord,
  showRecordOne,
  seasonRecordLoading,
  showSeasonRecord,
  recordFailedEmpty,
  recordFailedOne,
  summary,
  matches,
  matchesFailed,
  onRetryRecentMatches,
  onRetrySummary,
  season,
  comp,
  team,
  setSeason,
  setComp,
  setTeam,
  seasonOptions,
  onMatchDeleted,
}) {
  // PR #846 fix round, B3 (#760) / issue #762 — the neutral loading frame:
  // while EITHER gate can't yet tell empty/one-match/season apart from
  // populated, nothing populated-only (the bell, the menus) may render.
  if (recordLoading || seasonRecordLoading) {
    return <RecordEmptyScreen loading />;
  }
  // Issue #760 — the empty record.
  if (showEmptyRecord) {
    return <RecordEmptyScreen />;
  }
  // Issue #761 — the record after one match. See `renderRecordOne`'s own
  // header for why the specific match's own detail (not `summary` alone)
  // decides its three-way split.
  if (showRecordOne) {
    // Issue #900 — the one-match record's own delete now bumps the same
    // two reload keys a season-record delete does (`onMatchDeleted`'s own
    // comment above); a delete here drops the account to zero matches,
    // and `RecordEmptyScreen` is what the next render resolves to once
    // `summary.matches`/`matches.length` both catch up.
    return renderRecordOne({ summary, matches, matchesFailed, onRetryRecentMatches, onMatchDeleted });
  }
  // Issue #762 — the season record, absorbing "My Stats" per Decision 11
  // (2026-09-18). Every figure comes from `matches` itself, filtered by
  // its own season/competition/team state. R1 (PR #880 review) — its own
  // "career totals didn't load" banner reads `matchesFailed` (the SAME
  // `GET /matches` failure `renderRecordOne` above already threads
  // through), never `summaryFailed`/`/stats/summary` — that fetch has no
  // bearing on this screen's own figures, so a `/stats/summary`-only
  // outage used to show a banner claiming "the tiles below show a dash"
  // while every tile kept its real, matches-derived number.
  if (showSeasonRecord) {
    return (
      <SeasonRecordScreen
        matches={matches}
        matchesFailed={matchesFailed}
        onRetryMatches={onRetryRecentMatches}
        season={season}
        comp={comp}
        team={team}
        setSeason={setSeason}
        setComp={setComp}
        setTeam={setTeam}
        seasonOptions={seasonOptions}
      />
    );
  }
  // Issue #1217 — `/stats/summary` failed for a player with 0 or 1 matches:
  // the drawn failure states (D118), never the shipped dashboard. After the
  // season branch on purpose: both need `matches` to have loaded, and the
  // season record owns `matchesFailed` and 2+ matches.
  if (recordFailedEmpty) {
    return <RecordEmptyScreen failed onRetry={onRetrySummary} />;
  }
  if (recordFailedOne) {
    return (
      <RecordOneMatchScreen
        summary={null}
        match={matches[matches.length - 1]}
        summaryFailed
        onRetrySummary={onRetrySummary}
        onMatchDeleted={onMatchDeleted}
      />
    );
  }
  return null;
}

/** Issue #762 ruling 4 — `null` once the season record owns filtering; see
 * the call site's own comment for why this stays a function rather than an
 * inline `&&`. */
function renderSnapshotFilters({ seasonRecordOn, comp, season, team, setComp, setSeason, setTeam, compOptions, seasonOptions, teamOptions }) {
  if (seasonRecordOn) return null;
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <span className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Snapshot filters</span>
      <Select value={comp} onValueChange={setComp}>
        <SelectTrigger className="w-56" data-testid="dash-filter-comp"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All competitions</SelectItem>
          {compOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={season} onValueChange={setSeason}>
        <SelectTrigger className="w-44" data-testid="dash-filter-season"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All seasons</SelectItem>
          {seasonOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={team} onValueChange={setTeam}>
        <SelectTrigger className="w-44" data-testid="dash-filter-team"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All teams</SelectItem>
          {teamOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color, tid }) {
  return (
    <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all" data-testid={tid}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{title}</p>
            <div className="font-display font-black text-4xl mt-2" style={{ color }}>{value}</div>
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${color}15` }}>
            <Icon size={20} style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- Upcoming activity components ----------

const PRIORITY_STYLE = {
  high:   { dot: "#EF4444", label: "High" },
  medium: { dot: "#F59E0B", label: "Med" },
  low:    { dot: "#10B981", label: "Low" },
};

function MiniCount({ label, value, color }) {
  return (
    <div className="rounded-lg p-2 text-center" style={{ background: `${color}0F` }}>
      <div className="font-display font-black text-lg leading-none" style={{ color }}>{value}</div>
      <div className="text-[9px] uppercase tracking-widest text-slate-500 mt-0.5">{label}</div>
    </div>
  );
}

function fmtDate(d) {
  if (!d) return "";
  try {
    const dt = new Date(d + "T00:00:00");
    if (isNaN(dt.getTime())) return d;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const diff = Math.round((dt - today) / (1000 * 60 * 60 * 24));
    if (diff === 0) return "Today";
    if (diff === 1) return "Tomorrow";
    if (diff > 0 && diff < 7) return dt.toLocaleDateString("en-NZ", { weekday: "short" });
    return dt.toLocaleDateString("en-NZ", { month: "short", day: "numeric" });
  } catch {
    return d;
  }
}

function ActivityRow({ a }) {
  const prio = PRIORITY_STYLE[a.priority] || PRIORITY_STYLE.medium;
  return (
    <Link
      to={a.link || "/dashboard"}
      className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 hover:border-[#0B2E59] hover:bg-slate-50 transition group"
      data-testid={`upcoming-${a.kind}-${a.id}`}
    >
      <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ background: a.color }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[10px] uppercase tracking-widest font-bold" style={{ color: a.color }}>{fmtDate(a.date)}</span>
          {a.time && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><Clock size={9} /> {a.time}</span>}
          <span className="w-1 h-1 rounded-full" style={{ background: prio.dot }} title={`${prio.label} priority`} />
        </div>
        <div className="font-semibold text-slate-900 text-sm truncate">{a.title}</div>
        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
          {a.location && <span className="flex items-center gap-0.5 truncate"><MapPin size={10} /> {a.location}</span>}
          {a.subtitle && !a.location && <span className="truncate">{a.subtitle}</span>}
        </div>
      </div>
      <ChevronRight size={14} className="text-slate-300 group-hover:text-slate-900 mt-1.5 flex-shrink-0" />
    </Link>
  );
}
